import { AppError } from "../errors/types";

export function checkMicSupport(): { supported: boolean; reason?: string } {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    return { supported: false, reason: "このブラウザは音声入力に対応していません" };
  }
  return { supported: true };
}

// STT 精度改善 (2026-07-07 A 対応、`docs/decisions/2026-06-21-stt-provider-elevenlabs.md` 追記参照):
//   - noiseSuppression / echoCancellation / autoGainControl: 環境ノイズ・エコー・音量ばらつきを OS/browser 側で抑制
//   - channelCount=1 (mono) / sampleRate=48000: ElevenLabs Scribe v2 が期待する 16kHz+ mono に合わせる
//     (WebM/Opus は 48kHz で吐かれるので、サーバ側で downsample しない)
// これは MediaStreamConstraints の "advisory" (ブラウザが対応してなくても getUserMedia は成功する)。
// 対応しない古いブラウザではフォールバックで audio:true と同等の挙動になる。
// VAD (spec: docs/superpowers/specs/2026-07-07-m3-continuous-conversation-requirements.md §4.2 Q11):
// dev overlay の ?ns=off で noise suppression を切って raw signal 挙動を検証できるようにする。
// 他の processing (AEC/AGC) は on 維持。
// DOM lib の MediaTrackConstraints はグローバル型として ESLint が見つけないので構造リテラルで型付け
function buildConstraints(deviceId?: string) {
  const searchParams =
    typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const nsOff = searchParams?.get("ns") === "off";
  return {
    audio: {
      ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
      noiseSuppression: !nsOff,
      echoCancellation: true,
      autoGainControl: true,
      channelCount: 1,
      sampleRate: 48000,
    },
  };
}

export async function requestMicPermission(deviceId?: string): Promise<MediaStream> {
  const support = checkMicSupport();
  if (!support.supported) {
    throw new AppError("mic-not-supported", support.reason ?? "マイクが使えません");
  }
  try {
    return await navigator.mediaDevices.getUserMedia(buildConstraints(deviceId));
  } catch (err) {
    const name = (err as Error)?.name;
    if (name === "NotAllowedError" || name === "PermissionDeniedError") {
      throw new AppError("mic-permission-denied", "マイクの利用が許可されていません");
    }
    if (name === "OverconstrainedError") {
      if (deviceId) {
        // deviceId {exact} 指定時に fallback すると「選択したデバイス」が黙って
        // OS デフォルトへ差し替わるため、fallback せずエラーとして返す (Preflight 側で案内)
        throw new AppError("mic-no-audio", "選択したマイクが使えませんでした");
      }
      try {
        return await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (fallbackErr) {
        const fbName = (fallbackErr as Error)?.name;
        if (fbName === "NotAllowedError" || fbName === "PermissionDeniedError") {
          throw new AppError("mic-permission-denied", "マイクの利用が許可されていません");
        }
        throw new AppError("mic-no-audio", "マイクが見つかりませんでした");
      }
    }
    throw new AppError("mic-no-audio", "マイクが見つかりませんでした");
  }
}
